import React, { useEffect, useState } from 'react';
import { Alert, Box, List, ListItem, ListItemIcon, ListItemText, Paper, Typography } from '@mui/material';
import { CheckCircle, WarningAmber } from '@mui/icons-material';
import { apiFetch } from '../../utils/apiFetch.js';

// Pricing readiness checklist driven by the backend's validateMoverPricing —
// the backend stays authoritative; this component only renders its result.
export default function PricingReadiness({ token, refreshKey }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const headers = token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {};
    apiFetch('/api/moverPricing/me', { headers })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('Unable to load pricing status'))))
      .then(data => setStatus(data.pricingStatus || null))
      .catch(err => setError(err.message));
  }, [token, refreshKey]);

  if (error) return <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>;
  if (!status) return null;

  const checks = [
    { key: 'hourlyRate', label: 'Base hourly rate', ok: !status.missingFields?.includes('hourlyRate') && !status.invalidFields?.includes('hourlyRate') },
    { key: 'minimumHours', label: 'Minimum booking hours', ok: !status.missingFields?.includes('minimumHours') && !status.invalidFields?.includes('minimumHours') },
  ];
  const ready = status.valid;

  return (
    <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, border: '1px solid', borderColor: ready ? 'success.light' : 'warning.light', borderRadius: 2.5, bgcolor: ready ? 'success.50' : 'warning.50' }}>
      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>
        {ready ? '✓ Pricing setup complete' : 'Pricing setup incomplete'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {ready
          ? 'Customers can receive accurate quotes from MOVD.'
          : 'Complete these items before you can receive bookings.'}
      </Typography>
      <List dense disablePadding>
        {checks.map(check => (
          <ListItem key={check.key} disableGutters>
            <ListItemIcon sx={{ minWidth: 34 }}>
              {check.ok ? <CheckCircle color="success" fontSize="small" /> : <WarningAmber color="warning" fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={check.label} />
          </ListItem>
        ))}
      </List>
      {status.source === 'mover_company_legacy' && (
        <Typography variant="caption" color="text.secondary">
          Using rates from your company profile — save pricing here for full control.
        </Typography>
      )}
    </Paper>
  );
}
